import { z } from 'zod'

export const STATE_OPTIONS = {
	active: 'active',
	inactive: 'inactive',
} as const

export type StateOption = (typeof STATE_OPTIONS)[keyof typeof STATE_OPTIONS]

const recordStateValues = Object.values(STATE_OPTIONS) as [
	StateOption,
	...StateOption[],
]

export const RecordStateSchema = z.enum(recordStateValues, {
	message: 'El estado no es válido',
})

/** `''` is the Todos tab. Any other value must be a `StateOption`. */
export const RecordStateFilterSchema = RecordStateSchema.or(z.literal(''))

export function isStateOption(
	value: string | undefined | null,
): value is StateOption {
	return (
		value != null && (recordStateValues as readonly string[]).includes(value)
	)
}

/** Unknown or missing values render and persist as inactive. */
export function normalizeState(value: string | undefined | null): StateOption {
	return isStateOption(value) ? value : STATE_OPTIONS.inactive
}

/**
 * Canonicalizes a tabbed list's query in one pass.
 * A missing or invalid `state` becomes `active`. An empty `search` is removed.
 */
export function parseStateFilter({
	url,
	basePath,
}: {
	url: URL
	basePath: string
}): { redirectTo: string } | { state: '' | StateOption; search: string } {
	const params = new URLSearchParams(url.search)
	const search = params.get('search')
	const parsed = RecordStateFilterSchema.safeParse(params.get('state'))
	if (!parsed.success) params.set('state', STATE_OPTIONS.active)
	if (search === '') params.delete('search')

	if (!parsed.success || search === '') {
		const query = params.toString()
		return { redirectTo: query ? `${basePath}?${query}` : basePath }
	}

	return { state: parsed.data, search: search ?? '' }
}

/** Opt-in filter for selectors that always want active records. Omit on tabbed lists and historical reads. */
export const activeStateWhere = { state: STATE_OPTIONS.active } as const

/** Active records, plus the ids a selector already holds. An empty list is no selection. */
export function activeOrSelectedWhere(
	selected?: string | readonly (string | null | undefined)[] | null,
) {
	const ids =
		selected == null || selected === ''
			? []
			: typeof selected === 'string'
				? [selected]
				: selected.filter((id): id is string => Boolean(id))
	if (ids.length === 0) return activeStateWhere
	return { OR: [activeStateWhere, { id: { in: ids } }] }
}

export const stateOptionsLabels: Record<StateOption, string> = {
	active: 'Activo',
	inactive: 'Inactivo',
}

/** Plural copy for list filters. A new `StateOption` has to name its tab here. */
const stateFilterTabLabels: Record<StateOption, string> = {
	active: 'Activos',
	inactive: 'Inactivos',
}

/** Todos plus one tab per `STATE_OPTIONS` value, in definition order. */
export const stateFilterTabs = [
	{ displayName: 'Todos', value: undefined, filter: 'state' },
	...recordStateValues.map((value) => ({
		displayName: stateFilterTabLabels[value],
		value,
		filter: 'state',
	})),
]

export const stateOptions = Object.keys(STATE_OPTIONS).map((value) => ({
	value,
	label: stateOptionsLabels[value as StateOption],
}))
